<script setup>
import { ref, reactive } from 'vue';
import {useForm} from "@inertiajs/inertia-vue3";
import {Inertia} from "@inertiajs/inertia";
import {
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue';
import CandidateIcon from "@/Shared/SVG/CandidateIcon";
import RainbooIcon from "@/Shared/SVG/RainbooIcon";
import { XMarkIcon } from '@heroicons/vue/24/solid';
import Locations from "@/Shared/Locations";

let props = defineProps({
    data: Object,
    errors: Object
});

let form = useForm({
    full_name: null,
    email: null,
    subject: null,
    message: null,
    type: null,
})

let submitSettings = reactive({
    submitModalShow: false,
    submitted: false
})

function submit() {
    Inertia.post('/submit-contact', form, {
        onSuccess: () => {
            submitSettings.submitted = true
            submitSettings.submitModalShow = true
        }
    })
}
</script>

<template>
    <TransitionRoot as="template" class="z-50" :show="submitSettings.submitModalShow">
        <Dialog as="div" class="relative z-10">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div
                    class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div
                    class="flex min-h-full justify-center sm:p-4 text-center sm:items-center sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel
                            class="relative transform sm:rounded-2xl bg-white w-full sm:max-w-3xl transition-all"
                        >
                            <div class="bg-white bg-opacity-20 sm:bg-opacity-0 px-4 pt-6 sm:px-10">
                                <div class="text-center">
                                    <div v-if="submitSettings.submitModalShow">
                                        <div class="flex justify-end">
                                            <button @click.prevent="submitSettings.submitModalShow = false">
                                                <XMarkIcon class="text-gray-600 h-6 w-6"></XMarkIcon>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="flex flex-col m-5 sm:m-0 px-4 pt-5 pb-4 sm:px-10 sm:pt-0 sm:pb-10">
                                <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                    <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                        <div class="overflow-hidden">
                                            <div>
                                                <CandidateIcon class="m-auto w-[240px]"/>
                                                <p class="text-2xl -mt-20 px-12 mb-2 text-wgn-purple-2">
                                                    Thank you!
                                                </p>
                                                <p class="text-sm text-black text-opacity-70 px-32 mb-8">
                                                    We’ve received your message
                                                </p>
                                                <button @click.prevent="submitSettings.submitModalShow = false" class="px-8 py-4 text-white bg-wgn-purple-3 rounded-[40px] m-auto">
                                                    <span>Done</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
    <div class="w-full relative">
        <h1 class="text-white text-3xl font-bold mt-12 sm:mt-16 text-center px-2 max-w-lg m-auto font-bold">Contact</h1>
        <div class="mt-20 mb-20 relative" v-if="!submitSettings.submitted">
            <form class="bg-white rounded-2xl py-14 px-16 flex flex-col max-w-5xl m-auto" @submit.prevent="submit">
                <span class="text-black mb-8 text-2xl font-medium text-center">Fill the form to contact us</span>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-6">
                    <div class="col-span-2 md:col-span-1">
                        <input type="text" id="first_name" v-model="form.full_name" placeholder="Full Name" class="p-3 mt-1 block w-full h-12 rounded border border-wgn-blue-2 border-opacity-20 focus:border-wgn-blue-1 focus:ring-wgn-blue-1 text-sm">
                        <div v-if="errors.full_name" class="text-red-600 text-xs">
                            {{ errors.full_name }}
                        </div>
                    </div>
                    <div class="col-span-2 md:col-span-1">
                        <input type="text" id="email" v-model="form.email" placeholder="Email address" class="p-3 mt-1 block w-full h-12 rounded border border-wgn-blue-2 border-opacity-20 focus:border-wgn-blue-1 focus:ring-wgn-blue-1 text-sm">
                        <div v-if="errors.email" class="text-red-600 text-xs">
                            {{ errors.email }}
                        </div>
                    </div>

                    <div class="col-span-2">
                        <input type="text" id="subject" v-model="form.subject" placeholder="Subject" class="p-3 mt-1 block w-full h-12 rounded border border-wgn-blue-2 border-opacity-20 focus:border-wgn-blue-1 focus:ring-wgn-blue-1 text-sm">
                        <div v-if="errors.subject" class="text-red-600 text-xs">
                            {{ errors.subject }}
                        </div>
                    </div>
                    <div class="col-span-2">
                        <select id="type" v-model="form.type" class="rounded border border-wgn-blue-2 border-opacity-20 focus:border-wgn-blue-1 focus:ring-wgn-blue-1 text-sm block w-full p-2.5">
                            <option value="null" disabled selected>Select Contact Type</option>
                            <option v-for="type in data.contactsType" :value="type.value">{{type.label}}</option>
                        </select>
                        <div v-if="errors.type" class="text-red-600 text-xs">
                            {{ errors.type }}
                        </div>
                    </div>
                    <div class="col-span-2">
                        <textarea
                            name="message"
                            type="text"
                            rows="4"
                            v-model="form.message"
                            placeholder="Message"
                            class="block p-3 w-full rounded border border-wgn-blue-2 border-opacity-20 focus:border-wgn-blue-1 focus:ring-wgn-blue-1 text-sm">
                        </textarea>
                        <div v-if="errors.message" class="text-red-600 text-xs">
                            {{ errors.message }}
                        </div>
                    </div>
                </div>
                <div class="mt-5 flex">
                    <button type="submit" class="px-8 py-4 text-white bg-wgn-purple-3 rounded-[40px] m-auto">Submit</button>
                </div>

            </form>
        </div>
        <!--            -->
        <div class="mt-20 w-full border-b border-wgn-purple-4">
            <RainbooIcon class="m-auto"/>
        </div>
        <div class="mt-3">
            <h2 class="text-white text-[22px] mb-7 text-center">Locations</h2>
        </div>

        <div class="mb-20">
            <Locations :locations="data.locations"></Locations>
        </div>
    </div>
</template>
